import AjaxService from '../services/ajax-service';

import { handleServerError } from 'shared-services/error-service';
import { normalizeDateForServer } from '../utils/date';

export const fetchTasks = ({ userId, projectId, taskGroupId, taskId, milestoneId, archived, completed, offset, limit, order }) => {
    return AjaxService({
        method: 'get',
        url: 'tasks',
        params: {
            member_id: userId,
            project_id: projectId,
            task_group_id: taskGroupId,
            task_id: taskId,
            milestone_id: milestoneId,
            archived,
            completed,
            offset,
            limit,
            order
        }
    })
    .then(response => response.data)
    .catch(handleServerError);
}

export const archiveTasks = task_ids => {
    return AjaxService({
        method: 'post',
        url: `tasks/archive`,
        data: { task_ids }
    })
    .then(response => response.data)
    .catch(handleServerError);
}

export const completeTasks = task_ids => {
    return AjaxService({
        method: 'post',
        url: `tasks/complete`,
        data: { task_ids }
    })
    .then(response => response.data)
    .catch(handleServerError);
}

export const createTask = ({ task_group, task, name, description, milestone, assignees, due_date, reminder, order, story_points }) => {
    return AjaxService({
        method: 'post',
        url: 'tasks',
        data: {
            task_group_id: task_group,
            task_id: task,
            name,
            description,
            milestone_id: milestone,
            assignees,
            due_date: due_date ? normalizeDateForServer(due_date) : due_date,
            reminder: reminder ? normalizeDateForServer(reminder) : reminder,
            order: order === Number.POSITIVE_INFINITY ? undefined : order,
            story_points
        }
    })
    .then(response => response.data)
    .catch(handleServerError);
}

export const fetchTask = taskId => {
    return AjaxService({
        method: 'get',
        url: `tasks/${taskId}`
    })
    .then(response => response.data);
}

export const updateTask = (taskId, { name, description, milestone, due_date, reminder, task_group, task, order, dependency, completed, archived, story_points }) => {
    if (typeof completed !== 'undefined' && !task_group) {
        return updateCompletedState(taskId, completed);
    }

    if (typeof archived !== 'undefined' && !task_group) {
        return updateArchivedState(taskId, archived);
    }

    if (typeof milestone !== 'undefined') {
        return updateMilestoneDep(taskId, milestone);
    }

    return AjaxService({
        method: 'put',
        url: `tasks/${taskId}`,
        data: {
            name,
            description,
            milestone_id: milestone,
            due_date: due_date ? normalizeDateForServer(due_date) : due_date,
            reminder: reminder ? normalizeDateForServer(reminder) : reminder,
            task_group_id: task_group,
            task_id: task,
            order,
            dependency,
            completed,
            archived,
            story_points
        }
    })
    .then(response => response.data)
    .catch(handleServerError);
}

const updateCompletedState = (taskId, completed) => {
    return AjaxService({
        method: 'post',
        url: `tasks/${taskId}/complete`,
        data: { completed }
    })
    .then(response => response.data)
    .catch(handleServerError);
}

const updateArchivedState = (taskId, archived) => {
    return AjaxService({
        method: 'post',
        url: `tasks/${taskId}/archive`,
        data: { archived }
    })
    .then(response => response.data)
    .catch(handleServerError);
}

const updateMilestoneDep = (taskId, milestone_id) => {
    return AjaxService({
        method: 'put',
        url: `tasks/${taskId}/milestone`,
        data: { milestone_id }
    })
    .then(response => response.data)
    .catch(handleServerError);
}

export const moveTask = (taskId, { type, id, order }) => {
    return AjaxService({
        method: 'post',
        url: `tasks/${taskId}/move`,
        data: {
            to: { type, id },
            order
        }
    })
    .then(response => response.data)
    .catch(handleServerError);
}

export const removeTask = taskId => {
    return AjaxService({
        method: 'delete',
        url: `tasks/${taskId}`
    })
    .then(response => response.data)
    .catch(handleServerError);
}

export const removeArchivedTasks = project_id => {
    return AjaxService({
        method: 'delete',
        url: `tasks`,
        params: {
            project_id,
            archived: true
        }
    })
    .then(response => response.data)
    .catch(handleServerError);
}

export const assignUsers = (taskId, assignees) => {
    return AjaxService({
        method: 'put',
        url: `tasks/${taskId}/assignees`,
        data: {
            assignees
        }
    })
    .then(response => response.data)
    .catch(handleServerError);
}

export const unassignUsers = (taskId, assignees) => {
    return AjaxService({
        method: 'delete',
        url: `tasks/${taskId}/assignees`,
        data: {
            assignees
        }
    })
    .then(response => response.data)
    .catch(handleServerError);
}

export const setVisibility = (taskId, visible, roles) => {
    return AjaxService({
        method: 'post',
        url: `tasks/${taskId}/${visible ? 'reveal' : 'hide'}`,
        data: { roles }
    })
    .then(response => response.data)
    .catch(handleServerError);
}

export const addTaskLabels = (taskId, labels) => {
    return AjaxService({
        method: 'put',
        url: `tasks/${taskId}/labels`,
        data: { labels }
    })
    .then(response => response.data)
    .catch(handleServerError);
}

export const removeTaskLabel = (taskId, labelId) => {
    return AjaxService({
        method: 'delete',
        url: `tasks/${taskId}/labels`,
        data: {
            labels: [labelId]
        }
    })
    .then(response => response.data)
    .catch(handleServerError);
}

export const addTaskDoc = (taskId, document_id) => {
    return AjaxService({
        method: 'put',
        url: `tasks/${taskId}/documents`,
        data: { document_id }
    })
    .then(response => response.data)
    .catch(handleServerError);
}

export const removeTaskDoc = (taskId, document_id) => {
    return AjaxService({
        method: 'delete',
        url: `tasks/${taskId}/documents`,
        data: { document_id }
    })
    .then(response => response.data)
    .catch(handleServerError);
}

export const duplicateTask = (taskId, task_group_id) => {
    return AjaxService({
        method: 'post',
        url: `tasks/${taskId}/clone`,
        data: { task_group_id }
    })
    .then(response => response.data)
    .catch(handleServerError);
}

export const exportTasks = (start, end) => {
    return AjaxService({
        method: 'post',
        url: `tasks/export`,
        data: {
            start: normalizeDateForServer(start),
            end: normalizeDateForServer(end)
        }
    })
    .then(response => response.data)
    .catch(handleServerError);
}
